import {Button, Center, Loader, Text, TextInput} from "@mantine/core";
import { Contact } from "../types/Contact";
import { closeAllModals, openModal } from "@mantine/modals";
import React, { FC, useState } from "react";
import { useForm } from "@mantine/form";
import { isDefined } from "../Utils/utils";
import { ContactS } from "../Service/restapi/contactService";
import { i18n as coreI18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import type { Language } from "../types/BookitupDocument";
import { getDocumentTranslator } from "../Templates/Document/documentLanguage";

interface Props {
  contact: Contact;
  profileId: number;
  documentType: 'CONTRACT' | 'OFFER' | 'QUESTIONNAIRE';
  language?: Language;
  onFinish: (contact: Contact) => unknown;
}

const getDocumentLabel = (documentType: Props["documentType"]) => {
  if (documentType === "CONTRACT") {
    return { subject: "Vertrag", object: "den Vertrag" };
  }
  if (documentType === "QUESTIONNAIRE") {
    return { subject: "Fragebogen", object: "den Fragebogen" };
  }
  return { subject: "Angebot", object: "das Angebot" };
};

export const openCustomerAddressModal = (props: Props) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl">
          {props.documentType === "OFFER" || props.documentType === "CONTRACT"
            ? getDocumentTranslator(coreI18n, props.language)(
                props.documentType === "OFFER"
                  ? "completeOfferAddressTitle"
                  : "completeContractAddressTitle"
              )
            : `${getDocumentLabel(props.documentType).subject} bestätigen`}
      </Text>
    ),
    children: <CustomerAddressModal {...props} />,
  });

const CustomerAddressModal: FC<Props> = ({ contact, onFinish, profileId, documentType, language }) => {
  const { id, addressStreetAndNo, zipCode, city, phoneNumber, partner } = contact;
  const [loading, setLoading] = useState(false);
  const documentLabel = getDocumentLabel(documentType);
  const { i18n } = useLingui();
  const t = getDocumentTranslator(i18n, language);
  const isOffer = documentType === "OFFER";
  const isContract = documentType === "CONTRACT";
  const usesDocumentLanguage = isOffer || isContract;

  const form = useForm({
    initialValues: { addressStreetAndNo, zipCode, city, phoneNumber, partnerFirstName : partner?.firstName, partnerLastName : partner?.lastName },
    validate: {
      addressStreetAndNo: (val: string) =>
        isDefined(val) ? null : usesDocumentLanguage ? t("requiredField") : "Bitte Wert eingeben",
      zipCode: (val: string) => (isDefined(val) ? null : usesDocumentLanguage ? t("requiredField") : "Bitte Wert eingeben"),
      city: (val: string) => (isDefined(val) ? null : usesDocumentLanguage ? t("requiredField") : "Bitte Wert eingeben"),
    },
  });

  const onSubmit = async () => {
    setLoading(true);
    const { values } = form;
    if (partner && (partner.firstName == null || partner.lastName == null)) {
        await ContactS.edit(partner.id, {firstName: values.partnerFirstName, lastName: values.partnerLastName}, profileId);
    }
    const resJSON = await ContactS.edit(id, {addressStreetAndNo : values.addressStreetAndNo, zipCode : values.zipCode, city : values.city, phoneNumber: values.phoneNumber}, profileId);
    setLoading(false);
    if (resJSON) {
      closeAllModals();
      onFinish(resJSON);
    }
  };

  const askPartnerName = partner && (!partner.firstName || !partner.lastName);

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Text mb="md">
        {usesDocumentLanguage
          ? t(isOffer ? "completeOfferAddressText" : "completeContractAddressText")
          : `Bitte vervollständige die folgenden Angaben, um ${documentLabel.object} zu bestätigen:`}
      </Text>
      {askPartnerName && <TextInput required mt="sm" label={usesDocumentLanguage ? t("partnerFirstName") : "Partner Vorname"} {...form.getInputProps("partnerFirstName")} />}
      {askPartnerName && <TextInput required mt="sm" label={usesDocumentLanguage ? t("partnerLastName") : "Partner Nachname"} {...form.getInputProps("partnerLastName")} />}
      <TextInput
        required
        mt="sm"
        label={usesDocumentLanguage ? t("streetAndHouseNumber") : "Straße und Hausnummer"}
        {...form.getInputProps("addressStreetAndNo")}
      />
      <TextInput required mt="sm" label={usesDocumentLanguage ? t("zipCode") : "PLZ"} {...form.getInputProps("zipCode")} />
      <TextInput required mt="sm" label={usesDocumentLanguage ? t("city") : "Stadt"} {...form.getInputProps("city")} />
      <TextInput required mt="sm" label={usesDocumentLanguage ? t("phoneNumber") : "Telefonnummer"} {...form.getInputProps("phoneNumber")} />

        <Center mt={40}>
        <Button
          disabled={loading || !form.isDirty()}
          type="submit"
          rightIcon={loading && <Loader size="xs" />}
        >
          {usesDocumentLanguage
            ? t(isOffer ? "saveAndAcceptOfferButton" : "saveAndConfirmContractButton")
            : `Speichern und ${documentLabel.subject} bestätigen`}
        </Button>
      </Center>
    </form>
  );
};
